import React, { useState, useEffect, useRef } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { Save, ExternalLink, CheckCircle, AlertCircle, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API   = process.env.REACT_APP_BACKEND_URL + '/api';
const TOKEN = () => localStorage.getItem('auth_token') || '';

const PAGES = [
  { group: 'Stage 1 Pages', items: [
    { label: 'Home',              route: '/',                    slug: 'home' },
    { label: 'Newsroom',          route: '/newsroom',            slug: 'newsroom' },
    { label: 'Career Hub',        route: '/career-hub',          slug: 'career-hub' },
    { label: 'First Briefcase',   route: '/first-briefcase',     slug: 'first-briefcase' },
    { label: 'Post a Job',        route: '/career-hub/post',     slug: 'career-hub/post' },
    { label: 'Newsletter',        route: '/newsletter',          slug: 'newsletter' },
    { label: 'Podcast',           route: '/podcast',             slug: 'podcast' },
    { label: 'About',             route: '/about',               slug: 'about' },
    { label: 'Contact',           route: '/contact',             slug: 'contact' },
    { label: 'Subscribe',         route: '/subscribe',           slug: 'subscribe' },
  ]},
  { group: 'Coverage Pages (/coverage/*)', items: [
    { label: 'Education',        route: '/coverage/education',        slug: 'coverage/education' },
    { label: 'Business & Finance', route: '/coverage/business-finance', slug: 'coverage/business-finance' },
    { label: 'Healthcare',       route: '/coverage/healthcare',       slug: 'coverage/healthcare' },
    { label: 'Government',       route: '/coverage/government',       slug: 'coverage/government' },
    { label: 'Technology & AI',  route: '/coverage/technology-ai',    slug: 'coverage/technology-ai' },
    { label: 'Workforce & Labor', route: '/coverage/workforce-labor', slug: 'coverage/workforce-labor' },
  ]},
];

function isImageKit(url) {
  return url.startsWith('https://ik.imagekit.io/');
}

function PageRow({ item, initialUrl }) {
  const [val, setVal]           = useState(initialUrl || '');
  const [saving, setSaving]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const fileRef                 = useRef(null);
  const hasVal  = val.trim().length > 0;
  const isValid = !hasVal || isImageKit(val.trim());

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error('File too large (max 20 MB)'); return; }
    setUploading(true); setUploadPct(0);
    try {
      const authRes = await fetch(`${API}/imagekit/auth?folder=hero-images/`, {
        headers: { Authorization: `Bearer ${TOKEN()}` },
      });
      if (!authRes.ok) throw new Error('Failed to get upload credentials');
      const auth = await authRes.json();

      const form = new FormData();
      form.append('file', file);
      form.append('fileName', `${item.slug.replace(/\//g, '-')}-${Date.now()}.${file.name.split('.').pop()}`);
      form.append('publicKey', auth.publicKey);
      form.append('signature', auth.signature);
      form.append('expire', auth.expire);
      form.append('token', auth.token);
      form.append('folder', 'hero-images/');

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', 'https://upload.imagekit.io/api/v1/files/upload');
        xhr.upload.onprogress = ev => {
          if (ev.lengthComputable) setUploadPct(Math.round((ev.loaded / ev.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const data = JSON.parse(xhr.responseText);
            setVal(data.url);
            toast.success(`${item.label} image uploaded`);
            resolve(data);
          } else reject(new Error('Upload failed'));
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(form);
      });
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const save = async () => {
    if (hasVal && !isValid) { toast.error('Only ImageKit URLs are accepted (https://ik.imagekit.io/…)'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/page-hero/${item.slug}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${TOKEN()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ hero_background_image_url: val.trim() }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Save failed'); }
      toast.success(`${item.label} hero saved`);
    } catch (e) { toast.error(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: '1px solid rgba(221,212,197,0.35)' }}
      data-testid={`hero-row-${item.slug.replace(/\//g, '-')}`}>
      {/* Page info */}
      <div style={{ width: 170, flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#07121f' }}>{item.label}</div>
        <div style={{ fontSize: 11, color: '#66706D', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
          {item.route}
          <a href={item.route} target="_blank" rel="noopener noreferrer" style={{ color: '#008C86', lineHeight: 0 }}><ExternalLink size={10} /></a>
        </div>
      </div>

      {/* URL input + upload button */}
      <div style={{ flex: 1, display:'flex', flexDirection:'column', gap:6 }}>
        <div style={{ position: 'relative', display:'flex', gap:6, alignItems:'center' }}>
          {hasVal && (
            <button
              onClick={() => setVal('')}
              title="Clear URL"
              data-testid={`hero-clear-${item.slug.replace(/\//g,'-')}`}
              style={{ background: 'rgba(220,38,38,.08)', border: '1px solid rgba(220,38,38,.2)', borderRadius: 6, width: 28, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, color: '#DC2626' }}
            >
              ✕
            </button>
          )}
          <div style={{ flex:1, position:'relative' }}>
            <input
              value={val}
              onChange={e => setVal(e.target.value)}
              placeholder="https://ik.imagekit.io/… (leave empty to keep default)"
              data-testid={`hero-input-${item.slug.replace(/\//g, '-')}`}
              style={{
                width: '100%', padding: '8px 36px 8px 11px', border: `1px solid ${hasVal && !isValid ? 'rgba(220,38,38,.5)' : 'rgba(221,212,197,0.7)'}`,
                borderRadius: 8, fontSize: 12, boxSizing: 'border-box', background: '#fff', outline: 'none',
                color: hasVal && !isValid ? '#DC2626' : '#07121f',
              }}
            />
            {hasVal && (
              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', lineHeight: 0 }}>
                {isValid
                  ? <CheckCircle size={13} color="#008C86" />
                  : <AlertCircle size={13} color="#DC2626" />}
              </span>
            )}
          </div>
          {/* ImageKit upload */}
          <label title="Upload image directly to ImageKit" data-testid={`hero-upload-btn-${item.slug.replace(/\//g,'-')}`} style={{
            display:'inline-flex', alignItems:'center', gap:5, padding:'7px 12px', borderRadius:8, fontSize:12, fontWeight:600,
            background: uploading ? 'rgba(27,107,114,.1)' : '#F5F0E8', border:'1px solid #ddd8cc',
            color:'#1B6B72', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0,
          }}>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} style={{ display:'none' }} disabled={uploading} />
            {uploading ? `${uploadPct}%` : <><Upload size={12} /> Upload</>}
          </label>
        </div>
        {uploading && (
          <div style={{ height:3, background:'#e5e0d6', borderRadius:2, overflow:'hidden' }}>
            <div style={{ width:`${uploadPct}%`, height:'100%', background:'#1B6B72', transition:'width .2s' }} />
          </div>
        )}
      </div>

      {/* Preview */}
      {hasVal && isValid && (
        <img src={val.trim()} alt="thumb" style={{ width: 52, height: 36, objectFit: 'cover', borderRadius: 5, border: '1px solid rgba(221,212,197,0.5)', flexShrink: 0 }}
          onError={e => { e.target.style.display = 'none'; }} />
      )}

      {/* Save */}
      <button onClick={save} disabled={saving || (hasVal && !isValid)}
        data-testid={`hero-save-${item.slug.replace(/\//g, '-')}`}
        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#081D30', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: (saving || (hasVal && !isValid)) ? 'not-allowed' : 'pointer', flexShrink: 0, opacity: (hasVal && !isValid) ? 0.5 : 1 }}>
        <Save size={11} /> {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

export default function AdminHeroImagesPage() {
  const [configs, setConfigs] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/page-hero`)
      .then(r => r.json())
      .catch(() => ({}))
      .then(data => { setConfigs(data); setLoading(false); });
  }, []);

  return (
    <AdminLayout title="Site Controls" breadcrumb="Hero Images">
      <div style={{ maxWidth: 900 }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontFamily: "'Inter Tight', sans-serif", fontSize: 20, fontWeight: 800, color: '#07121f', letterSpacing: '-.05em', margin: '0 0 4px' }}>
            Hero Image CMS
          </h1>
          <p style={{ fontSize: 13, color: '#66706D', margin: 0 }}>
            Paste an ImageKit URL to override the hero background on each page. Leave empty to keep the default design. Only <code style={{ fontSize: 11, background: 'rgba(221,212,197,0.4)', padding: '1px 5px', borderRadius: 4 }}>https://ik.imagekit.io/</code> URLs are accepted.
          </p>
        </div>

        {loading ? (
          <div style={{ padding: 32, color: '#66706D', fontSize: 13 }}>Loading…</div>
        ) : (
          PAGES.map(group => (
            <div key={group.group} style={{ background: '#fff', border: '1px solid rgba(221,212,197,0.7)', borderRadius: 12, padding: '18px 22px', marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.09em', color: '#008C86', marginBottom: 10 }}>
                {group.group}
              </div>
              {group.items.map(item => (
                <PageRow key={item.slug} item={item} initialUrl={configs[item.slug] || ''} />
              ))}
            </div>
          ))
        )}

        <div style={{ fontSize: 11, color: '#66706D', marginTop: 8, lineHeight: 1.6 }}>
          Coverage pages support future routes added under <code style={{ fontSize: 10, background: 'rgba(221,212,197,0.4)', padding: '1px 4px', borderRadius: 3 }}>/coverage/*</code> automatically — add the slug to the API and it will be picked up.
        </div>
      </div>
    </AdminLayout>
  );
}
